import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { artUrl } from "@/lib/uploadArt";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import TagPicker from "@/components/studio/TagPicker";
import { tagClasses } from "@/lib/tags";
import { useUsage, UsageBadges } from "@/components/marketing/UsageBadges";
import MarketingComposer from "@/components/marketing/MarketingComposer";
import { Megaphone, UploadCloud, Search, Trash2, Tag as TagIcon, Download, Loader2, ImageIcon, Pencil, X, Wand2, Sparkles, Clock } from "lucide-react";
import { toast } from "sonner";

const isImg = (ct) => (ct || "").startsWith("image/");
const daysLeft = (iso) => iso ? Math.ceil((new Date(iso) - new Date()) / 86400000) : null;
const POST_PLATFORMS = [
  { id: "ig_feed", label: "Instagram Feed" },
  { id: "ig_portrait", label: "Instagram Portrait" },
  { id: "story", label: "Story / Reel" },
  { id: "tiktok", label: "TikTok" },
  { id: "fb_feed", label: "Facebook Feed" },
  { id: "whatsapp", label: "WhatsApp" },
];

export default function Marketing() {
  const [view, setView] = useState("assets"); // assets | posts
  const [assets, setAssets] = useState([]);
  const [facets, setFacets] = useState({ categories: [], ratios: [], sizes: [], tags: [], total: 0 });
  const [category, setCategory] = useState("");
  const [ratio, setRatio] = useState("");
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");
  const [postPlatform, setPostPlatform] = useState("");
  const [postMonth, setPostMonth] = useState("");
  const [postQ, setPostQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [upOpen, setUpOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [composeAsset, setComposeAsset] = useState(null);
  const [selected, setSelected] = useState([]);

  const loadFacets = () => api.get("/marketing/facets").then(({ data }) => setFacets(data)).catch(() => {});
  const load = useCallback(() => {
    setLoading(true);
    const params = { generated: view === "posts" ? "true" : "false" };
    if (view === "assets") {
      if (category) params.category = category;
      if (ratio) params.ratio = ratio;
      if (tag) params.tag = tag;
      if (q) params.q = q;
    } else {
      if (postPlatform) params.platform = postPlatform;
      if (postMonth) params.month = postMonth;
      if (postQ) params.q = postQ;
    }
    api.get("/marketing/assets", { params }).then(({ data }) => setAssets(data)).catch(() => {}).finally(() => setLoading(false));
  }, [view, category, ratio, tag, q, postPlatform, postMonth, postQ]);

  useEffect(() => { loadFacets(); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setSelected([]); }, [view]);

  const afterChange = () => { load(); loadFacets(); };

  const changeTags = async (a, tags) => {
    setAssets((xs) => xs.map((x) => x.id === a.id ? { ...x, tags } : x));
    try { await api.put(`/marketing/assets/${a.id}`, { tags }); loadFacets(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); load(); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.filename}"?`)) return;
    try { await api.delete(`/marketing/assets/${a.id}`); toast.success("Deleted"); afterChange(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} selected post${selected.length === 1 ? "" : "s"}?`)) return;
    try { await api.post("/marketing/assets/bulk-delete", { ids: selected }); toast.success(`${selected.length} deleted`); setSelected([]); afterChange(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const usage = useUsage(assets.map((a) => a.file_id));

  return (
    <div data-testid="marketing-page">
      <PageHeader eyebrow="Marketing" title="Marketing Studio" subtitle="Your design bank + post composer — organize artwork and turn it into ready-to-share posts." testid="marketing-header">
        {view === "assets"
          ? <div className="flex gap-2"><Button variant="outline" onClick={() => setComposeAsset({ __new: true })} data-testid="marketing-new-post"><Wand2 size={16} className="mr-1.5" /> New post</Button><Button onClick={() => setUpOpen(true)} data-testid="marketing-upload-btn"><UploadCloud size={16} className="mr-1.5" /> Upload designs</Button></div>
          : selected.length > 0 && <Button variant="destructive" onClick={bulkDelete} data-testid="marketing-bulk-delete"><Trash2 size={16} className="mr-1.5" /> Delete selected ({selected.length})</Button>}
      </PageHeader>

      <div className="p-8 space-y-5">
        {/* View tabs */}
        <div className="flex gap-2" data-testid="marketing-view-tabs">
          <button onClick={() => setView("assets")} className={`text-sm px-4 py-2 rounded-lg border ${view === "assets" ? "border-[#2495D3] bg-[#2495D3]/10 text-[#2495D3] font-semibold" : "border-slate-200 text-slate-500"}`} data-testid="marketing-tab-assets">Assets</button>
          <button onClick={() => setView("posts")} className={`text-sm px-4 py-2 rounded-lg border ${view === "posts" ? "border-[#2495D3] bg-[#2495D3]/10 text-[#2495D3] font-semibold" : "border-slate-200 text-slate-500"}`} data-testid="marketing-tab-posts">Generated posts <span className="opacity-60">· 30 days</span></button>
        </div>

        {view === "assets" && (
          <div className="flex flex-wrap items-center gap-3" data-testid="marketing-filters">
            <div className="relative">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search filename…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-52" data-testid="marketing-search" />
            </div>
            <select className="h-10 rounded-lg border px-2 text-sm bg-white" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="marketing-category-filter">
              <option value="">All categories</option>{facets.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="h-10 rounded-lg border px-2 text-sm bg-white" value={ratio} onChange={(e) => setRatio(e.target.value)} data-testid="marketing-ratio-filter">
              <option value="">All ratios</option>{facets.ratios.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="h-10 rounded-lg border px-2 text-sm bg-white" value={tag} onChange={(e) => setTag(e.target.value)} data-testid="marketing-tag-filter">
              <option value="">All tags</option>{facets.tags.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <span className="text-xs text-slate-400 font-mono ml-auto">{assets.length} assets</span>
          </div>
        )}

        {view === "posts" && (
          <div className="flex flex-wrap items-center gap-3" data-testid="marketing-post-filters">
            <div className="relative">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder='Search e.g. "tiktok"…' value={postQ} onChange={(e) => setPostQ(e.target.value)} className="pl-8 w-52" data-testid="post-search" />
            </div>
            <select className="h-10 rounded-lg border px-2 text-sm bg-white" value={postPlatform} onChange={(e) => setPostPlatform(e.target.value)} data-testid="post-platform-filter">
              <option value="">All platforms</option>
              {POST_PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <input type="month" className="h-10 rounded-lg border px-2 text-sm bg-white" value={postMonth} onChange={(e) => setPostMonth(e.target.value)} data-testid="post-month-filter" />
            {(postPlatform || postMonth || postQ) && <button onClick={() => { setPostPlatform(""); setPostMonth(""); setPostQ(""); }} className="text-xs text-slate-400 hover:text-[#2495D3]" data-testid="post-clear-filters">Clear</button>}
            <span className="text-xs text-slate-400 font-mono ml-auto">{assets.length} posts</span>
          </div>
        )}

        {loading ? (
          <div className="text-center text-slate-400 py-20">Loading…</div>
        ) : assets.length === 0 ? (
          <div className="text-center text-slate-400 py-20" data-testid="marketing-empty">
            <Megaphone size={40} className="mx-auto mb-3 opacity-50" />
            {view === "assets" ? "No designs yet. Click “Upload designs” to add artwork in bulk." : "No posts generated yet. Open a design in Assets and click “Create post”."}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="marketing-grid">
            {assets.map((a) => {
              const dl = daysLeft(a.expires_at);
              const sel = selected.includes(a.id);
              return (
                <div key={a.id} className={`rounded-xl border bg-white overflow-hidden group ${sel ? "border-[#2495D3] ring-2 ring-[#2495D3]/30" : "border-slate-200"}`} data-testid={`asset-card-${a.id}`}>
                  <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden relative">
                    {isImg(a.content_type)
                      ? <img src={artUrl(a.file_id)} alt={a.filename} className="w-full h-full object-contain" loading="lazy" />
                      : <div className="flex flex-col items-center text-slate-400 text-xs"><ImageIcon size={28} /><span className="mt-1 uppercase">{a.filename?.split(".").pop()}</span></div>}
                    {view === "posts" && (
                      <input type="checkbox" checked={sel} onChange={() => toggleSel(a.id)} className="absolute top-2 left-2 w-4 h-4" data-testid={`post-select-${a.id}`} />
                    )}
                    <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <a href={artUrl(a.file_id)} target="_blank" rel="noreferrer" download className="bg-white/90 rounded p-1 text-slate-600 hover:text-[#2495D3]" title="Download" data-testid={`asset-download-${a.id}`}><Download size={13} /></a>
                      {view === "assets" && <button onClick={() => setEdit(a)} className="bg-white/90 rounded p-1 text-slate-600 hover:text-[#2495D3]" title="Edit" data-testid={`asset-edit-${a.id}`}><Pencil size={13} /></button>}
                      <button onClick={() => remove(a)} className="bg-white/90 rounded p-1 text-slate-600 hover:text-red-500" title="Delete" data-testid={`asset-delete-${a.id}`}><Trash2 size={13} /></button>
                    </div>
                    {a.ratio && <span className="absolute bottom-1.5 left-1.5 text-[10px] font-mono bg-black/60 text-white px-1.5 py-0.5 rounded">{a.ratio}</span>}
                    {view === "posts" && dl !== null && <span className={`absolute bottom-1.5 right-1.5 text-[9px] font-mono px-1.5 py-0.5 rounded flex items-center gap-0.5 ${dl <= 7 ? "bg-red-600 text-white" : "bg-black/60 text-white"}`}><Clock size={9} />{dl > 0 ? `${dl}d` : "exp"}</span>}
                  </div>
                  <div className="p-2.5">
                    <div className="text-xs font-medium text-slate-700 truncate" title={a.filename}>{a.filename}</div>
                    <UsageBadges usage={usage[a.file_id]} className="mt-1" />
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-slate-400 truncate">{a.size_label || a.category || "—"}</span>
                      {view === "assets" && (
                        <Popover>
                          <PopoverTrigger asChild><button className="text-slate-400 hover:text-[#2495D3] p-0.5" title="Tags" data-testid={`asset-tags-${a.id}`}><TagIcon size={12} /></button></PopoverTrigger>
                          <PopoverContent className="w-64" data-testid={`asset-tags-pop-${a.id}`}>
                            <div className="text-[11px] font-mono uppercase text-slate-400 mb-2">Tags</div>
                            <TagPicker value={a.tags || []} onChange={(nt) => changeTags(a, nt)} testid={`asset-tagpicker-${a.id}`} />
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                    {view === "assets" && (
                      <Button size="sm" variant="outline" className="w-full mt-2 h-8 text-xs" onClick={() => setComposeAsset(a)} data-testid={`asset-compose-${a.id}`}><Wand2 size={12} className="mr-1" /> Create post</Button>
                    )}
                    {view === "posts" && a.caption && <p className="text-[10px] text-slate-400 mt-1 line-clamp-2">{a.caption}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <UploadDialog open={upOpen} onOpenChange={setUpOpen} facets={facets} onDone={afterChange} />
      <EditDialog asset={edit} onOpenChange={(v) => !v && setEdit(null)} onDone={afterChange} />
      <MarketingComposer asset={composeAsset} open={!!composeAsset} onOpenChange={(v) => !v && setComposeAsset(null)} onDone={() => { if (view !== "posts") setView("posts"); afterChange(); }} />
    </div>
  );
}

function UploadDialog({ open, onOpenChange, facets, onDone }) {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState("");
  const [sizeLabel, setSizeLabel] = useState("");
  const [tags, setTags] = useState([]);
  const [aiCat, setAiCat] = useState("");
  const [aiTags, setAiTags] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (!open) { setFiles([]); setCategory(""); setSizeLabel(""); setTags([]); setAiCat(""); setAiTags([]); } }, [open]);

  const autoFill = async () => {
    if (!files.length) { toast.error("Pick an image first"); return; }
    setAiBusy(true);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(files[0]); });
      const { data } = await api.post("/marketing/analyze", { image_base64: b64 });
      if (data.category) { setCategory(data.category); setAiCat(data.category); }
      if (data.size_label) setSizeLabel(data.size_label);
      if (Array.isArray(data.tags) && data.tags.length) { setTags(data.tags); setAiTags(data.tags); }
      toast.success("AI suggestions filled — review, correct & approve");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "AI auto-fill failed"); }
    finally { setAiBusy(false); }
  };

  const upload = async () => {
    if (!files.length) { toast.error("Pick at least one image"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("category", category);
      fd.append("size_label", sizeLabel);
      fd.append("tags", tags.join(","));
      fd.append("ai_category", aiCat);
      fd.append("ai_tags", aiTags.join(","));
      const { data } = await api.post("/marketing/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} design${data.created === 1 ? "" : "s"} uploaded`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="marketing-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload marketing designs</DialogTitle>
          <DialogDescription>Add several images at once. Use AI auto-fill to suggest category, size and tags — then approve.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <label onClick={() => inputRef.current?.click()} className="flex flex-col items-center justify-center gap-2 h-32 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer hover:border-[#2495D3] text-sm text-slate-500 bg-slate-50/50" data-testid="marketing-drop">
            <UploadCloud size={24} />
            <span>{files.length ? `${files.length} file${files.length === 1 ? "" : "s"} selected` : "Click to pick images (multiple)"}</span>
            <span className="text-[11px] text-slate-400">PNG / JPG / WEBP / PDF — up to 150MB each</span>
            <input ref={inputRef} type="file" multiple accept="image/*,application/pdf" className="hidden" data-testid="marketing-file-input" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          </label>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto" data-testid="marketing-selected">
              {files.map((f, i) => (
                <span key={i} className="text-[11px] bg-slate-100 rounded px-2 py-1 flex items-center gap-1 max-w-[160px]">
                  <span className="truncate">{f.name}</span>
                  <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><X size={11} /></button>
                </span>
              ))}
            </div>
          )}
          {files.length > 0 && (
            <Button size="sm" variant="outline" onClick={autoFill} disabled={aiBusy} className="w-full" data-testid="marketing-autofill">
              {aiBusy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />} Auto-fill with AI (from 1st image)
            </Button>
          )}
          <div>
            <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Category</label>
            <Input list="mk-cats" placeholder="e.g. Manotas, Food, Promos" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="marketing-up-category" />
            <datalist id="mk-cats">{facets.categories.map((c) => <option key={c} value={c} />)}</datalist>
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Size label (optional)</label>
            <Input list="mk-sizes" placeholder='e.g. 4x6 in, 12x12 in' value={sizeLabel} onChange={(e) => setSizeLabel(e.target.value)} data-testid="marketing-up-size" />
            <datalist id="mk-sizes">{facets.sizes.map((s) => <option key={s} value={s} />)}</datalist>
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Tags</label>
            <TagPicker value={tags} onChange={setTags} testid="marketing-up-tags" />
          </div>
          <p className="text-[11px] text-slate-400">Aspect ratio (1:1, 1:2, …) is detected automatically from each image.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={upload} disabled={busy} data-testid="marketing-up-submit">
            {busy ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <UploadCloud size={15} className="mr-1.5" />} Upload {files.length ? `(${files.length})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ asset, onOpenChange, onDone }) {
  const [category, setCategory] = useState("");
  const [sizeLabel, setSizeLabel] = useState("");
  const [tags, setTags] = useState([]);
  useEffect(() => { if (asset) { setCategory(asset.category || ""); setSizeLabel(asset.size_label || ""); setTags(asset.tags || []); } }, [asset]);
  const save = async () => {
    try { await api.put(`/marketing/assets/${asset.id}`, { category, size_label: sizeLabel, tags }); toast.success("Updated"); onOpenChange(false); onDone(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!asset} onOpenChange={onOpenChange}>
      <DialogContent data-testid="marketing-edit-dialog">
        <DialogHeader><DialogTitle>Edit asset</DialogTitle>
          <DialogDescription>Update the category, size label and tags for this design.</DialogDescription>
        </DialogHeader>
        {asset && (
          <div className="space-y-4">
            <div className="flex gap-3 items-center">
              {isImg(asset.content_type) && <img src={artUrl(asset.file_id)} alt={asset.filename} className="w-16 h-16 object-contain rounded-lg border" />}
              <div className="text-xs text-slate-500 min-w-0"><div className="truncate font-medium text-slate-700">{asset.filename}</div><div>{asset.ratio}{asset.width ? ` · ${asset.width}×${asset.height}px` : ""}</div></div>
            </div>
            <div><label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Category</label><Input value={category} onChange={(e) => setCategory(e.target.value)} data-testid="marketing-edit-category" /></div>
            <div><label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Size label</label><Input value={sizeLabel} onChange={(e) => setSizeLabel(e.target.value)} data-testid="marketing-edit-size" /></div>
            <div><label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Tags</label><TagPicker value={tags} onChange={setTags} testid="marketing-edit-tags" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} data-testid="marketing-edit-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
